import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../../firebase/firebaseConfig';
import { useActiveFamily } from '../../../context/ActiveFamilyContext';
import { PlanComparisonResult } from '../../../services/planComparisonService';
import { DowngradeSelections, DowngradeRecommendations } from '../../../services/downgradeService';

interface ShoppingList {
  id: string;
  name: string;
  itemCount?: number;
  updatedAt?: any;
}

interface ShoppingListSelectionStepProps {
  comparison: PlanComparisonResult;
  recommendations: DowngradeRecommendations | null;
  selections: DowngradeSelections;
  onUpdateSelection: (key: keyof DowngradeSelections, value: any) => void;
}

export default function ShoppingListSelectionStep({
  comparison,
  recommendations,
  selections,
  onUpdateSelection,
}: ShoppingListSelectionStepProps) {
  const { activeFamily } = useActiveFamily();
  const [shoppingLists, setShoppingLists] = useState<ShoppingList[]>([]);
  const [loading, setLoading] = useState(true);
  
  const selectedId = selections.activeShoppingListId;
  const isValid = !!selectedId;
  
  useEffect(() => {
    loadShoppingLists();
  }, [activeFamily?.id]);
  
  const loadShoppingLists = async () => {
    if (!activeFamily?.id) return;
    
    setLoading(true);
    try {
      const q = query(
        collection(db, 'shopping_lists'),
        where('familyId', '==', activeFamily.id)
      );
      const snapshot = await getDocs(q);
      const items = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as ShoppingList[];
      
      setShoppingLists(items);
      
      // Auto-seleccionar recomendada si hay
      if (!selectedId && recommendations?.shoppingListId) {
        onUpdateSelection('activeShoppingListId', recommendations.shoppingListId);
      }
    } catch (error) {
      console.error('Error cargando listas:', error);
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Selecciona 1 Lista de Compras
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          El plan gratuito permite 1 lista activa. Las demás se pausarán (no se borran).
        </p>
      </div>
      
      <div className={`rounded-xl p-4 border-2 ${
        isValid 
          ? 'bg-green-50 dark:bg-green-900/20 border-green-500' 
          : 'bg-blue-50 dark:bg-blue-900/20 border-blue-500'
      }`}>
        <div className="flex items-center gap-3">
          <div className="text-3xl">{isValid ? '✅' : '🛒'}</div>
          <div>
            <div className="font-semibold text-gray-900 dark:text-white">
              {isValid ? 'Lista seleccionada' : 'Selecciona una lista'}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              {isValid ? '¡Perfecto! Puedes continuar' : 'Elige la lista más importante'}
            </div>
          </div>
        </div>
      </div>
      
      <div className="space-y-2">
        {shoppingLists.length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            No tienes listas de compras
          </div>
        ) : (
          shoppingLists.map(list => {
            const isSelected = selectedId === list.id;
            const isRecommended = recommendations?.shoppingListId === list.id;
            
            return (
              <button
                key={list.id}
                onClick={() => onUpdateSelection('activeShoppingListId', list.id)}
                className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                  isSelected
                    ? 'bg-purple-50 dark:bg-purple-900/20 border-purple-500 shadow-md'
                    : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-700 hover:shadow-md'
                }`}
              >
                <div className="flex items-center gap-4">
                  <div className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                    isSelected
                      ? 'bg-purple-600 border-purple-600'
                      : 'border-gray-300 dark:border-gray-600'
                  }`}>
                    {isSelected && (
                      <div className="w-3 h-3 bg-white rounded-full"></div>
                    )}
                  </div>
                  
                  <div className="text-2xl">🛒</div>
                  
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {list.name}
                      </span>
                      {isRecommended && (
                        <span className="px-2 py-0.5 text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded-full font-medium">
                          🔸 RECOMENDADO
                        </span>
                      )}
                    </div>
                    {list.itemCount !== undefined && (
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        {list.itemCount} items
                      </div>
                    )}
                  </div>
                </div>
              </button>
            );
          })
        )}
      </div>
      
      {shoppingLists.length > 1 && (
        <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4 border-2 border-amber-200 dark:border-amber-800">
          <div className="flex items-start gap-3">
            <span className="text-2xl">ℹ️</span>
            <div className="text-sm text-gray-700 dark:text-gray-300">
              <p className="font-medium mb-1">
                ⏸️ {shoppingLists.length - 1} lista(s) se pausarán
              </p>
              <p>Podrás reactivarlas al renovar tu plan.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
